import IReadingPlan from "../interfaces/IReadingPlan";
import IReadingPlanRecord from "../interfaces/IReadingPlanRecord";
import ReadingPlanSessionTypes from "./ReadingPlanSessionTypes";
import ReadingPlanTypes from "./ReadingPlanTypes";

type ReadingPlanType = {
    bible: 'BIBLE',
    ccrbrp: 'CCRBRP',
    dditit: 'DDITIT',
    m52gbc: 'M52GBC',
}
export const ReadingPlanCodes: ReadingPlanType = {
    bible: 'BIBLE',
    ccrbrp: 'CCRBRP',
    dditit: 'DDITIT',
    m52gbc: 'M52GBC',
}

export type ReadingPlanCodeValues = typeof ReadingPlanCodes[keyof typeof ReadingPlanCodes]

export const AllReadingPlanCodes: ReadingPlanCodeValues[] = Array.from(Object.values(ReadingPlanCodes));

export const ReadingPlans:{ [key in ReadingPlanCodeValues]: IReadingPlan } = {
    [ReadingPlanCodes.bible]: {
        id: 1,
        readingPlan: 'No Plan',
        readingPlanCode: ReadingPlanCodes.bible,
        readingPlanType: ReadingPlanTypes.whole,
        sessionsTotal: 1189,
        completionTime: 'At your own pace',
        defaultSessionPerDay: 1,
        sessionsPerDayOptions: [
            {
                description: '1 Chapter per day (~ 3.25 years)',
                value: 1,
            }
        ],
        sessionsURL: '/assets/plans/BIBLE/',
        sessionsType: ReadingPlanSessionTypes.files,
        modifiedDate: new Date(1699905902298),
        readingPlanExtrasLink: 'https://biblestudytogether.com/printed-chronological-cross-reference-bible-reading-plan/',
        description: 'Read the Bible however you want.',
        shortDescription: 'Read the Bible however you want.',
    },
    [ReadingPlanCodes.ccrbrp]: {
        id: 2,
        readingPlan: 'Chronological Cross-Reference Plan',
        readingPlanCode: ReadingPlanCodes.ccrbrp,
        readingPlanType: ReadingPlanTypes.whole,
        sessionsTotal: 730,
        completionTime: '92 Days to 2 Years',
        defaultSessionPerDay: 1,
        sessionsPerDayOptions: [
            {
                description: '1 reading/day (finish in 2 years)',
                value: 1,
            },
            {
                description: '2 readings/day (finish in 1 year)',
                value: 2,
            },
            {
                description: '3 readings/day (finish in 8 months)',
                value: 3,
            },
            {
                description: '4 readings/day (finish in 6 months)',
                value: 4,
            },
            {
                description: '8 readings/day (finish in 92 days)',
                value: 8,
            },
        ],
        sessionsURL: '/assets/plans/CCRBRP/',
        sessionsType: ReadingPlanSessionTypes.files,
        modifiedDate: new Date(1699905902298),
        readingPlanExtrasLink: 'https://biblestudytogether.com/printed-chronological-cross-reference-bible-reading-plan/',
        description: `The Bible Study Together App currently uses the Chronological Cross-Reference Bible Reading Plan. It is designed to start on a Saturday, but can be started on whatever day you want. It is a plan that takes you through the entire Bible chronologically while regularly connecting NT and OT passages into the daily reading. Each reading typically takes less than 10 minutes. Below you can set the pace going through the plan ranging from 2 years to 92 days. Also, you can set your start date three different ways: select the day you want to start your reading plan, choose a specific day of the plan that you want to be your current day's reading, or enter a code from a group member to synchronize with their reading schedule.`,
        shortDescription: 'Read the whole Bible in chronological order with parallel New Testament passage. This plan helps you see how the New Testament fulfills the Old Testament in the daily reading. Its loaded with daily questions, videos, maps, and has accompanying resources in print too. With full audio Bible support you can make it through the whole Bible in 92 days, 6 months, 1 year, or 2 years.This plan is great for reading by yourself or in groups.',
    },
    [ReadingPlanCodes.dditit]: {
        id: 2,
        readingPlan: 'Daily Dive into Titus',
        readingPlanCode: ReadingPlanCodes.dditit,
        readingPlanType: ReadingPlanTypes.single,
        sessionsTotal: 28,
        completionTime: '28 Days',
        defaultSessionPerDay: 1,
        sessionsPerDayOptions: [
            {
                description: '1 reading/day (finish in 28 days)',
                value: 1,
            }
        ],
        sessionsURL: '/assets/plans/DDITIT/',
        sessionsType: ReadingPlanSessionTypes.files,
        modifiedDate: new Date(1711749946054),
        readingPlanExtrasLink: 'https://biblestudytogether.com/printed-chronological-cross-reference-bible-reading-plan/',
        description: `Each day we will be reading from Titus, digging deeper with study questions, and then gleaning insights about the questions in the daily videos. Over the course of this four-week study, we will first focus on getting the broad picture of what Paul is writing in the book and then we will continue to focus down on specific details. The first week we will be laying the foundation by reading through the book and getting introduced to the key points of this letter. Then each following week we will focus on one chapter for the week.  Our format is uniquely designed to accompany a regular meeting with your Bible study groups or to enhance a local church's expository sermon series.`,
        shortDescription: `Our "Daily Dive" format is designed help you first get the big picture of the book and then each week to focus into a particular chapter. Daily open-ended questions help you dig into the reading and the accompanying videos help you get the most out of the questions. Though you can use this plan yourself, it works particularly well with regularly meeting groups and can greatly enhance your local church's expository sermon series on Titus.`,
    },
    [ReadingPlanCodes.m52gbc]: {
        id: 2,
        readingPlan: `Mike's 52 Greatest Bible Chapters`,
        readingPlanCode: ReadingPlanCodes.m52gbc,
        readingPlanType: ReadingPlanTypes.single,
        sessionsTotal: 364,
        completionTime: '364 Days',
        defaultSessionPerDay: 1,
        sessionsPerDayOptions: [
            {
                description: '1 reading/day (finish in 364 days)',
                value: 1,
            }
        ],
        sessionsURL: '/assets/plans/M52GBC/',
        sessionsType: ReadingPlanSessionTypes.files,
        modifiedDate: new Date(1731710912744),
        readingPlanExtrasLink: 'https://biblestudytogether.com/printed-chronological-cross-reference-bible-reading-plan/',
        description: `"Mike's 52 Greatest Bible Chapters" is a year-long reading plan featuring Pastor Mike Harrison's handpicked favorite chapters, guiding you through pivotal passages to grasp key biblical concepts. Read one chapter daily for a week, completing the series in 52 weeks. We have added daily reflection questions to accompany each chapter for discussion with your groups and encouraging you to dig deeper. While the audio component is currently limited to the English Standard Version (ESV), you're free to read the chapters in your preferred Bible translation, allowing for flexibility and personal preference. Embark on this transformative journey and experience the richness of Scripture in a new way.`,
        shortDescription: `"Mike's 52 Greatest Bible Chapters" is a year-long reading plan featuring Pastor Mike Harrison's handpicked favorite chapters. Read one chapter daily for a week, completing the series in 52 weeks. This plan guides you through pivotal passages, helping you grasp key biblical concepts, develop a daily scripture-reading habit, and deepen your understanding of God's word.`,
    },
}

export const createDefaultReadingPlanRecord = (planCode: ReadingPlanCodeValues) => {
    const nowDateTime = new Date(Date.now());
    const completedSessions = Array.from({length: ReadingPlans[planCode].sessionsTotal}, () => false);

    const defaultReadingPlanRecord: IReadingPlanRecord = {
        id: 0,
        readingPlan: ReadingPlans[planCode].readingPlan,
        active: true,
        startDate: nowDateTime,
        sessionsPerDay: ReadingPlans[planCode].defaultSessionPerDay,
        completedSessions,
        modifiedDate: nowDateTime,
        sessionsURL: ReadingPlans[planCode].sessionsURL,
        sessionsType: ReadingPlans[planCode].sessionsType,
        readingPlanCode: ReadingPlans[planCode].readingPlanCode,
        readingPlanExtrasLink: ReadingPlans[planCode].readingPlanExtrasLink,
        name: ReadingPlans[planCode] === ReadingPlans.BIBLE ? 'Read The Bible' : 'My Reading Plan',
        groupId: '',
        // syncId is undefined to indicate it has never been synced before
      } 

    return defaultReadingPlanRecord
}

export default ReadingPlans;